import { cancelRun, fetchRun, fetchRunEvents } from '@/api/sender';
import { resumePanelSenderRun } from '@/api/sender-panel';
import { senderRoutes } from '@/lib/sender-routes';
import { Spinner } from '@/components/ui/spinner';
import { useSenderRunDetail } from '@/hooks/use-sender-run-detail';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { SenderScreen } from '@/pages/sender/sender-screen';
import SenderRunDetailView from '@/pages/sender/sender-run-detail-view';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

export default function SenderRunDetailPage() {
	return (
		<SenderScreen title='Запуск'>
			<RunDetailContent />
		</SenderScreen>
	);
}

function RunDetailContent() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [cancelling, setCancelling] = useState(false);
	const [resuming, setResuming] = useState(false);

	const loadRun = useCallback(async () => {
		if (!id) throw new Error('missing run id');
		return fetchRun(id);
	}, [id]);

	const loadEvents = useCallback(async () => {
		if (!id) throw new Error('missing run id');
		const data = await fetchRunEvents(id, { limit: 100 });
		return data.items;
	}, [id]);

	const {
		run,
		events,
		loading,
		loadData,
		bumpGeneration,
		applyOptimisticCancel,
		applyOptimisticResume,
	} = useSenderRunDetail({
		enabled: Boolean(id),
		loadRun,
		loadEvents,
	});

	const handleResume = useCallback(async () => {
		if (!id) return;
		setResuming(true);
		try {
			await resumePanelSenderRun(id);
			bumpGeneration();
			applyOptimisticResume();
			toast.success('Рассылка возобновлена');
		} catch {
			// error toast handled by api interceptor
		} finally {
			setResuming(false);
		}
	}, [id, bumpGeneration, applyOptimisticResume]);

	const handleCancel = useCallback(async () => {
		if (!id) return;
		setCancelling(true);
		try {
			await cancelRun(id);
			bumpGeneration();
			applyOptimisticCancel();
			toast.success('Запуск отменён');
			await loadData();
		} catch {
			// error toast handled by api interceptor
		} finally {
			setCancelling(false);
		}
	}, [id, bumpGeneration, applyOptimisticCancel, loadData]);

	if (!id) {
		return <LandingsErrorState message='Запуск не найден' onRetry={() => navigate(senderRoutes.hub)} />;
	}

	if (loading) {
		return (
			<div className='flex justify-center py-16'>
				<Spinner />
			</div>
		);
	}

	if (!run) {
		return <LandingsErrorState onRetry={() => void loadData(true)} />;
	}

	return (
		<SenderRunDetailView
			run={run}
			events={events}
			actions={{
				onCancel: handleCancel,
				onResume: handleResume,
				cancelling,
				resuming,
			}}
		/>
	);
}
